import { getBlockedCards } from 'APIs/blockedcards/blockedcards';
import { BlockedCard, BlockedCardsQueryParams } from 'APIs/blockedcards/blockedcards.types';
import Modal from 'Components/Modal/Modal';
import Spinner from 'Components/Spinner/Spinner';
import { DateTime } from 'luxon';
import { FormEvent, MouseEvent as ReactMouseEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatAPICurrencyAmount } from 'utils/currency';

import { SpinnerWrapper } from './styles';
import { blockedCardEventTypeLabels } from './types';

type Props = {
  handleCancel: (e: ReactMouseEvent<HTMLButtonElement>) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  actionBlockedCard: BlockedCard | null;
};
const InfoModal = ({ handleCancel, handleSubmit, actionBlockedCard }: Props) => {
  const { apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [latestEvent, setLatestEvent] = useState<BlockedCard | null>(null);

  const fetchBlockedCard = useCallback(async () => {
    if (!apiAprilBaseUri) {
      return;
    }

    try {
      if (actionBlockedCard === null) {
        return;
      }
      setIsLoading(true);

      const qParams: BlockedCardsQueryParams = {
        sort: '-createdAt',
        limit: 1,
        page: 1,
        cardBlockId: [actionBlockedCard.cardBlockId],
      };

      const resp = await getBlockedCards(apiAprilBaseUri, qParams);
      setLatestEvent(resp.items[0]);
    } catch (e) {
      setLatestEvent(null);
    } finally {
      setIsLoading(false);
    }
  }, [actionBlockedCard, apiAprilBaseUri]);

  useEffect(() => {
    fetchBlockedCard();
  }, [fetchBlockedCard, actionBlockedCard]);

  return (
    <Modal
      title="Card Block Event Details"
      cancelBtnText="Close"
      confirmBtnText="Unblock Card"
      handleCancel={handleCancel}
      handleSubmit={handleSubmit}
      testId={'blocked-card-details-modal'}
      hideConfirmBtn={!actionBlockedCard?.isCardBlocked}
    >
      {isLoading && (
        <SpinnerWrapper>
          <Spinner color="#0016D1" />
        </SpinnerWrapper>
      )}
      {!isLoading && actionBlockedCard && (
        <div className="info">
          <div className="info-row">
            <div className="info-col">Event Type</div>
            <div className="info-col data-col">{blockedCardEventTypeLabels[actionBlockedCard.eventType].label}</div>
          </div>
          <div className="info-row">
            <div className="info-col">Card Brand & Number</div>
            <div className="info-col data-col">
              {actionBlockedCard.paymentMethod?.paymentMethodType === 'Card'
                ? `${actionBlockedCard.paymentMethod?.cardBrand} *${actionBlockedCard.paymentMethod.cardLast4}`
                : '-'}
            </div>
          </div>
          <div className="info-row">
            <div className="info-col">Card Funding</div>
            <div className="info-col data-col">
              {actionBlockedCard.paymentMethod?.paymentMethodType === 'Card'
                ? `${actionBlockedCard.paymentMethod?.cardFunding}`
                : '-'}
            </div>
          </div>
          <div className="info-row">
            <div className="info-col">Card Issuer</div>
            <div className="info-col data-col">
              {actionBlockedCard.paymentMethod?.paymentMethodType === 'Card'
                ? `${actionBlockedCard.paymentMethod?.cardIssuer}`
                : '-'}
            </div>
          </div>
          <div className="info-row">
            <div className="info-col">Card Country</div>
            <div className="info-col data-col">
              {actionBlockedCard.paymentMethod?.paymentMethodType === 'Card'
                ? `${actionBlockedCard.paymentMethod?.cardCountry}`
                : '-'}
            </div>
          </div>
          <div className="info-row">
            <div className="info-col">Merchant Name</div>
            <div className="info-col data-col">{actionBlockedCard.purchaseDetails?.merchantName ?? '-'}</div>
          </div>
          <div className="info-row">
            <div className="info-col">Merchant ID</div>
            <div className="info-col data-col">{actionBlockedCard.purchaseDetails?.merchantId ?? '-'}</div>
          </div>
          <div className="info-row">
            <div className="info-col">Description</div>
            <div className="info-col data-col">{actionBlockedCard.description}</div>
          </div>
          <div className="info-row">
            <div className="info-col">Reason</div>
            <div className="info-col data-col">{actionBlockedCard.blockUserMessage ?? '-'}</div>
          </div>
          <div className="info-row">
            <div className="info-col">Date and Time</div>
            <div className="info-col data-col">
              {DateTime.fromISO(actionBlockedCard.createdAt).toFormat('dd/LL/yyyy, HH:mm')}
            </div>
          </div>
          <div className="info-row">
            <div className="info-col">Transaction ID</div>
            <div className="info-col data-col">{actionBlockedCard.purchaseDetails?.transactionId ?? '-'}</div>
          </div>
          <div className="info-row">
            <div className="info-col">Order Reference ID</div>
            <div className="info-col data-col">{actionBlockedCard.purchaseDetails?.referenceOrderId ?? '-'}</div>
          </div>
          <div className="info-row">
            <div className="info-col">Transaction Amount</div>
            <div className="info-col data-col">
              {actionBlockedCard.purchaseDetails
                ? formatAPICurrencyAmount(actionBlockedCard.purchaseDetails.amount, { suffixCurrencyCode: true })
                : '-'}
            </div>
          </div>
          {latestEvent && (
            <>
              <div className="separator"></div>
              <div className="info-row">
                <div className="info-col">Currently Blocked</div>
                <div className="info-col data-col">{latestEvent.isCardBlocked ? 'Yes' : 'No'}</div>
              </div>
              <div className="info-row">
                <div className="info-col">Most recent event</div>
                <div className="info-col data-col">
                  <div>{blockedCardEventTypeLabels[latestEvent.eventType].label}</div>
                  <div>{`[${DateTime.fromISO(latestEvent.createdAt).toFormat('dd/LL/yyyy, HH:mm')}]`}</div>
                </div>
              </div>
              <div className="info-row">
                <div className="info-col">Description</div>
                <div className="info-col data-col">{latestEvent.description}</div>
              </div>
              <div className="info-row">
                <div className="info-col">Reason</div>
                <div className="info-col data-col">{latestEvent.blockUserMessage ?? '-'}</div>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default InfoModal;
